import React, { useMemo } from 'react'
import Dropzone from 'react-dropzone'
import { push } from 'connected-react-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import {
    Button,
    Classes,
    Dialog,
    FormGroup,
    Icon,
    InputGroup,
    MenuItem,
    Spinner,
    Tooltip,
} from '@blueprintjs/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileLines } from '@fortawesome/pro-duotone-svg-icons/faFileLines'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'

import InputSelect from 'src/components/InputSelect'
import MultiSelectInput from 'src/components/MultiSelectInput'
import * as runbookModules from 'src/store/modules/runbooks'
import * as paths from 'src/constants/paths'
import {
    searchCustomer,
    getCustomerById,
    getCustomerResults,
} from 'src/store/modules/cmdb/customers'
import { setActiveRunbook } from 'src/store/modules/ui/runbook'
import {
    RUNBOOKS_TEMPLATES_USE,
    isAllowed,
} from 'src/components/auth/permissions'
import { UserDataContext } from 'src/HOCs/withAuth'
import { LOADING_TEMPLATES, MUTUAL_EXCLUSION_TOOLTIP, NO_MATCHING_TEMPLATES, RUNBOOK_CATEGORY, validate } from './constants'

import styles from './createRunbookModal.module.scss'


/**
 * CreateRunbookModal component allows users to create a new runbook.
 *
 */
class CreateRunbookModal extends React.Component {
    static contextType = UserDataContext

    state = {
        acceptedFile: null,
        acceptedFileContent: null,
        solutions: [...runbookModules.MODES.ids],
        selectedTemplateId: null,
        selectedCategory: null,
    }

    componentDidMount() {
        const { actions } = this.props

        actions.searchCustomer('')
        this.fetchTemplates()
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.orgPk !== this.props.orgPk ||
            prevState.selectedCategory !== this.state.selectedCategory
        ) {
            this.fetchTemplates()
        }

        if (
            prevProps.selectedTemplateActions !==
                this.props.selectedTemplateActions &&
            this.state.selectedTemplateId !== null
        ) {
            this.props.actions.applyTemplateActions(
                this.props.runbookId,
                this.props.selectedTemplateActions
            )
        }
    }

    fetchTemplates = () => {
        const { actions, orgPk } = this.props
        const { selectedCategory } = this.state

        if (isAllowed(this.context, RUNBOOKS_TEMPLATES_USE)) {
            const organization = orgPk ? `${orgPk},all` : 'all'
            const category = selectedCategory ? `${selectedCategory},all` : 'all'

            actions.getRunbookTemplates({
                template_type: 'runbook_framework',
                organization,
                runbook_category: category,
                per_page: 1000,
            })
        }
    }

    updateRunbook = (data) => {
        const { runbookId, actions } = this.props

        actions.updateRunbook(runbookId, data)
    }

    modeRenderer = (item, { handleClick, modifiers }) => {
        const { orgPk } = this.props
        const { byId } = runbookModules.MODES
        // TODO: Make ALL_SOLUTIONS and BUSINESS_SOLUTIONS work as well
        const isDisabled =
            (!orgPk && item !== runbookModules.FREE_FORM) ||
            [
                runbookModules.ALL_SOLUTIONS,
                runbookModules.BUSINESS_SOLUTIONS,
            ].includes(item)

        return (
            <MenuItem
                disabled={isDisabled}
                active={modifiers.active}
                key={item}
                label={byId[item].description}
                onClick={handleClick}
                text={byId[item].text}
            />
        )
    }

    failoverParamRenderer = (number) => (item, { handleClick, modifiers }) => {
        return (
            <MenuItem
                active={modifiers.active}
                key={`${number}-${item}`}
                onClick={handleClick}
                text={item}
            />
        )
    }

    orgItemRenderer = (item, { handleClick, modifiers }) => {
        return (
            <MenuItem
                active={modifiers.active}
                key={item.id}
                label={item.id}
                onClick={handleClick}
                text={item.name}
            />
        )
    }

    renderOrgInputValue = (organization) => organization.name

    orgSelect = ({ name, id }) =>
        this.updateRunbook({
            organization: name,
            orgPk: id,
        })

    handleOrganizationSearch = (searchTerm) => {
        const { actions } = this.props

        this.updateRunbook({ organization: searchTerm })
        actions.searchCustomer(searchTerm)
    }

    handleFileSelect = (acceptedFiles) => {
        const file = acceptedFiles[0]
        const r = new FileReader()

        r.onload = (e) => {
            let acceptedFileContent = e.target.result
            this.setState({ acceptedFile: file, acceptedFileContent })
            // this.props.actions.generateFromCsv(this.props.selectedCustomer.id, contents)
            // Clear any selected template when a file is chosen
            if (this.state.selectedTemplateId !== null) {
                this.handleClearSelectedTemplate()
            }
        }

        if (file) {
            r.readAsText(file)
        } else {
            this.handleClearSelectedFile()
        }
    }

    handleClearSelectedFile = () => {
        this.setState({
            acceptedFile: null,
            acceptedFileContent: null,
        })
    }

    handleTemplateSelect = (template) => {
        const { actions } = this.props
        this.setState({ selectedTemplateId: template.id })
        this.updateRunbook({ templateId: template.id })

        actions.getRunbookTemplateActions({ templateId: template.id })

        // Clear any uploaded CSV when a template is chosen
        if (this.state.acceptedFile !== null) {
            this.handleClearSelectedFile()
        }
    }

    handleClearSelectedTemplate = () => {
        this.setState({ selectedTemplateId: null })
        this.updateRunbook({
            templateId: null,
            actions: {
                byId: {},
                uids: [],
                allUids: [],
                orphanedUids: [],
                ciToUid: {},
            },
            topAction: null,
        })
    }

    handleCategorySelect = (option) => {
        this.setState({
            selectedCategory: option.value,
            selectedTemplateId: null,
        })
        this.updateRunbook({
            templateId: null,
            actions: {
                byId: {},
                uids: [],
                allUids: [],
                orphanedUids: [],
                ciToUid: {},
            },
            topAction: null,
        })
    }

    handleParameterClick = (zone) => {
        const { parameters } = this.props

        if (parameters.indexOf(zone.name) > -1) {
            this.updateRunbook({
                parameters: parameters.filter((p) => p !== zone.name),
            })
        } else {
            this.updateRunbook({
                parameters: [...parameters, zone.name],
            })
        }
    }

    handleClearAllParameters = () => {
        this.updateRunbook({ parameters: [] })
    }

    handleCreateRunbook = () => {
        const {
            selectedCustomer,
            mode,
            runbookId,
            actions,
            dispatch,
        } = this.props

        actions.setActiveRunbook(null)
        dispatch(push(paths.NEW_PATH))

        if (this.state.acceptedFileContent) {
            actions.generateFromCsv(
                selectedCustomer.id,
                this.state.acceptedFile?.name,
                this.state.acceptedFileContent
            )
        } else if (
            [
                runbookModules.DR_SOLUTIONS_GENERIC,
                runbookModules.DR_SOLUTIONS_RESTART,
                runbookModules.DR_SOLUTIONS_FAILOVER,
            ].includes(mode)
        ) {
            actions.getSolutions(runbookId)
        }
    }

    renderParametrsItem = (item, { modifiers, handleClick }) => {
        if (!modifiers.matchesPredicate) {
            return null
        }

        return (
            <MenuItem
                active={modifiers.active}
                key={item.id}
                onClick={handleClick}
                text={item.name}
                shouldDismissPopover={false}
            />
        )
    }

    _renderNameFiled() {
        const { name } = this.props

        return (
            <FormGroup label="Runbook name" labelFor="name">
                <InputGroup
                    value={name}
                    id="name"
                    large
                    onChange={(e) =>
                        this.updateRunbook({
                            name: e.target.value,
                        })
                    }
                />
            </FormGroup>
        )
    }

    _renderOrganizationSelect() {
        const { organization, customersLoading, customers } = this.props

        return (
            <FormGroup label="Organization" labelFor="organization">
                <InputSelect
                    inputProps={{
                        id: 'organization',
                        large: true,
                        rightElement: customersLoading ? (
                            <Spinner size={Icon.SIZE_STANDARD} />
                        ) : null,
                    }}
                    selectedItem={{ name: organization || '' }}
                    items={customers}
                    itemRenderer={this.orgItemRenderer}
                    inputValueRenderer={this.renderOrgInputValue}
                    onItemSelect={this.orgSelect}
                    onQueryChange={this.handleOrganizationSearch}
                    noResults={<MenuItem disabled={true} text="No results" />}
                    popoverProps={{
                        minimal: true,
                        targetClassName: 'full-width',
                    }}
                    minimal={true}
                    additionalRightElement={
                        organization ? (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className={styles.clearInputIcon}
                                onClick={() =>
                                    this.updateRunbook({
                                        organization: '',
                                        orgPk: null,
                                    })
                                }
                            />
                        ) : null
                    }
                />
            </FormGroup>
        )
    }

    _renderRunbookTypeSelect() {
        const { solutions } = this.state
        const { mode } = this.props

        return (
            <FormGroup label="Runbook type" labelFor="runbook-type">
                <InputSelect
                    inputProps={{
                        id: 'runbook-type',
                        large: true,
                    }}
                    selectedItem={mode}
                    items={solutions}
                    itemRenderer={this.modeRenderer}
                    inputValueRenderer={(id) =>
                        runbookModules.MODES.byId[id].text
                    }
                    onItemSelect={(i) =>
                        this.updateRunbook({
                            mode: i,
                            parameters: [],
                        })
                    }
                    filterable={false}
                    noResults={<MenuItem disabled={true} text="No results" />}
                    popoverProps={{
                        targetClassName: 'full-width',
                        minimal: true,
                    }}
                    minimal={true}
                    additionalRightElement={
                        mode ? (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className={styles.clearInputIcon}
                                onClick={() =>
                                    this.updateRunbook({
                                        mode: null,
                                        parameters: [],
                                    })
                                }
                            />
                        ) : null
                    }
                />
            </FormGroup>
        )
    }

    _renderRunbookCategorySelect() {
        const { selectedCategory } = this.state

        const selectedOption =
            RUNBOOK_CATEGORY.find((o) => o.value === selectedCategory) || null

        return (
            <FormGroup label="Runbook category" labelFor="runbook-category">
                <InputSelect
                    inputProps={{
                        id: 'runbook-category',
                        large: true,
                    }}
                    selectedItem={selectedOption}
                    items={RUNBOOK_CATEGORY}
                    itemRenderer={(item, { handleClick, modifiers }) => (
                        <MenuItem
                            active={modifiers.active}
                            key={item.label}
                            onClick={handleClick}
                            text={item.label}
                        />
                    )}
                    inputValueRenderer={(item) => item.label}
                    onItemSelect={this.handleCategorySelect}
                    filterable={false}
                    noResults={<MenuItem disabled text="No results" />}
                    popoverProps={{
                        minimal: true,
                        targetClassName: 'full-width',
                    }}
                    minimal
                    additionalRightElement={
                        selectedOption ? (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className={styles.clearInputIcon}
                                onClick={() => {
                                    this.handleCategorySelect({
                                        value: null,
                                        label: 'Any',
                                    })
                                }}
                            />
                        ) : null
                    }
                />
            </FormGroup>
        )
    }

    _renderTemplateSelect() {
        const { runbookFrameworkTemplates, templatesLoading } = this.props
        const { selectedTemplateId, acceptedFile } = this.state
        const isDisabled = acceptedFile !== null

        const compatibleTemplates = runbookFrameworkTemplates

        const selectedTemplate =
            selectedTemplateId !== null
                ? compatibleTemplates.find(
                      (t) => t.id === selectedTemplateId
                  ) || null
                : null

        const templateRenderer = (item, { handleClick, modifiers }) => (
            <MenuItem
                active={modifiers.active}
                key={item.id}
                onClick={handleClick}
                text={item.name}
            />
        )

        const noResultsText = templatesLoading
            ? LOADING_TEMPLATES
            : NO_MATCHING_TEMPLATES

        const select = (
            <InputSelect
                inputProps={{
                    id: 'template-select',
                    large: true,
                    rightElement: !templatesLoading ? (
                        <Spinner size={Icon.SIZE_STANDARD} />
                    ) : null,
                    disabled: isDisabled,
                    placeholder: 'Select a template',
                }}
                disabled={isDisabled}
                selectedItem={selectedTemplate}
                items={compatibleTemplates}
                itemRenderer={templateRenderer}
                inputValueRenderer={(t) => t.name}
                onItemSelect={this.handleTemplateSelect}
                filterable={false}
                noResults={<MenuItem disabled={true} text={noResultsText} />}
                additionalRightElement={
                    selectedTemplate ? (
                        <FontAwesomeIcon
                            icon={faXmark}
                            className={styles.clearInputIcon}
                            onClick={this.handleClearSelectedTemplate}
                        />
                    ) : null
                }
                popoverProps={{
                    minimal: true,
                    targetClassName: 'full-width',
                    disabled: isDisabled,
                }}
                minimal={true}
            />
        )

        return (
            <FormGroup label="Use a template" labelFor="template-select">
                {isDisabled ? (
                    <Tooltip content={MUTUAL_EXCLUSION_TOOLTIP}>
                        <span className="full-width">{select}</span>
                    </Tooltip>
                ) : (
                    select
                )}
            </FormGroup>
        )
    }

    _renderAddFileDropzone() {
        const { acceptedFile } = this.state
        const isDisabled = this.state.selectedTemplateId !== null

        const acceptStyle = {
            borderColor: '#349f7b',
        }

        const rejectStyle = {
            borderColor: '#ff5151',
        }

        const dropzone = (
            <Dropzone
                onDrop={isDisabled ? undefined : this.handleFileSelect}
                noClick
                noKeyboard
                disabled={isDisabled}
                maxFiles={1}
                accept=".csv, application/vnd.ms-excel, text/csv"
            >
                {({
                    getRootProps,
                    getInputProps,
                    open,
                    isDragAccept,
                    isDragReject,
                }) => {
                    const style = useMemo(
                        () => ({
                            ...(isDragAccept ? acceptStyle : {}),
                            ...(isDragReject ? rejectStyle : {}),
                            ...(isDisabled
                                ? { opacity: 0.5, pointerEvents: 'none' }
                                : {}),
                        }),
                        [isDragReject, isDragAccept]
                    )

                    return (
                        <div
                            id="add-file"
                            className={styles.dropzone}
                            style={style}
                            {...getRootProps()}
                        >
                            <input {...getInputProps()} />

                            <FontAwesomeIcon
                                icon={faFileLines}
                                className={styles.fileIcon}
                            />

                            {acceptedFile ? (
                                <div className={styles.dropzoneAcceptedFile}>
                                    <div>
                                        <p className={styles.dropzoneFirstText}>
                                            {acceptedFile.name}
                                        </p>
                                        <p
                                            className={
                                                styles.dropzoneSecondText
                                            }
                                        >
                                            {acceptedFile.size} kb
                                        </p>
                                    </div>
                                    <Button
                                        intent="geyser"
                                        icon="trash"
                                        onClick={this.handleClearSelectedFile}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <p className={styles.dropzoneFirstText}>
                                        Drop your file here, or{' '}
                                        <span
                                            className={styles.dropzoneBrowse}
                                            onClick={open}
                                        >
                                            browse
                                        </span>
                                    </p>

                                    <p className={styles.dropzoneSecondText}>
                                        Allowed format: .csv.
                                    </p>
                                </div>
                            )}
                        </div>
                    )
                }}
            </Dropzone>
        )

        return (
            <FormGroup label="Add a file" labelFor="add-file">
                {isDisabled ? (
                    <Tooltip content={MUTUAL_EXCLUSION_TOOLTIP}>
                        <span>{dropzone}</span>
                    </Tooltip>
                ) : (
                    dropzone
                )}
            </FormGroup>
        )
    }

    render() {
        const {
            parameters,
            onCancel,
            mode,
            modalValid,
            selectedCustomer,
        } = this.props

        const canUseTemplates = isAllowed(this.context, RUNBOOKS_TEMPLATES_USE)

        return (
            <Dialog
                isOpen={true}
                onClose={() => onCancel()}
                title="Create new runbook"
            >
                <div className={Classes.DIALOG_BODY}>
                    {this._renderNameFiled()}
                    {this._renderOrganizationSelect()}
                    {this._renderRunbookTypeSelect()}
                    {canUseTemplates && this._renderRunbookCategorySelect()}
                    {canUseTemplates && this._renderTemplateSelect()}
                    {selectedCustomer && this._renderAddFileDropzone()}
                    {mode === runbookModules.DR_SOLUTIONS_RESTART && (
                        <FormGroup label="Parameters">
                            <MultiSelectInput
                                items={selectedCustomer.avZones.map(
                                    (zone, index) => ({
                                        id: index,
                                        name: zone,
                                    })
                                )}
                                fullWidth
                                large
                                tagRenderer={(item) => item.name}
                                itemRenderer={this.renderParametrsItem}
                                noResults={
                                    <MenuItem
                                        disabled={true}
                                        text="No results."
                                    />
                                }
                                tagInputProps={{
                                    placeholder: 'Select parameters',
                                }}
                                onItemSelect={this.handleParameterClick}
                                onDeselectItem={this.handleParameterClick}
                                onClearAll={this.handleClearAllParameters}
                            />
                        </FormGroup>
                    )}
                    {mode === runbookModules.DR_SOLUTIONS_FAILOVER && (
                        <FormGroup
                            label="Select From Parameter"
                            labelFor="select-from-parameter"
                        >
                            <InputSelect
                                inputProps={{
                                    id: 'select-from-parameter',
                                    large: true,
                                    placeholder: 'Select option',
                                }}
                                items={selectedCustomer.mainAvZones}
                                itemRenderer={this.failoverParamRenderer(1)}
                                filterable={false}
                                selectedItem={{
                                    name: parameters[0] ?? '',
                                }}
                                inputValueRenderer={(parameter) =>
                                    parameter.name
                                }
                                noResults={
                                    <MenuItem
                                        disabled={true}
                                        text="No results"
                                    />
                                }
                                popoverProps={{
                                    minimal: true,
                                    targetClassName: 'full-width',
                                }}
                                onItemSelect={(i) =>
                                    this.updateRunbook({
                                        parameters: [i, parameters[1]],
                                    })
                                }
                                additionalRightElement={
                                    parameters[0] ? (
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            className={styles.clearInputIcon}
                                            onClick={() =>
                                                this.updateRunbook({
                                                    parameters: [
                                                        null,
                                                        parameters[1],
                                                    ],
                                                })
                                            }
                                        />
                                    ) : null
                                }
                            />
                        </FormGroup>
                    )}

                    {mode === runbookModules.DR_SOLUTIONS_FAILOVER && (
                        <FormGroup
                            label="Select To Parameter"
                            labelFor="select-to-parameter"
                        >
                            <InputSelect
                                inputProps={{
                                    id: 'select-to-parameter',
                                    large: true,
                                    placeholder: 'Select option',
                                }}
                                items={selectedCustomer.mainAvZones}
                                itemRenderer={this.failoverParamRenderer(2)}
                                filterable={false}
                                selectedItem={{
                                    name: parameters[1] ?? '',
                                }}
                                inputValueRenderer={(parameter) =>
                                    parameter.name
                                }
                                noResults={
                                    <MenuItem
                                        disabled={true}
                                        text="No results"
                                    />
                                }
                                popoverProps={{
                                    minimal: true,
                                    targetClassName: 'full-width',
                                }}
                                onItemSelect={(i) =>
                                    this.updateRunbook({
                                        parameters: [parameters[0], i],
                                    })
                                }
                                additionalRightElement={
                                    parameters[0] ? (
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            className={styles.clearInputIcon}
                                            onClick={() =>
                                                this.updateRunbook({
                                                    parameters: [
                                                        null,
                                                        parameters[1],
                                                    ],
                                                })
                                            }
                                        />
                                    ) : null
                                }
                            />
                        </FormGroup>
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button
                            intent="primary"
                            large
                            disabled={!modalValid}
                            onClick={this.handleCreateRunbook}
                        >
                            Create runbook
                        </Button>
                        <Button
                            intent="lynch-underline"
                            large
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Dialog>
        )
    }
}

const mapStateToProps = (state) => {
    const { activeRunbook } = state.ui.runbook
    const runbook = runbookModules.getRunbook(state, activeRunbook)

    const customers = getCustomerResults(state)
    const selectedCustomer = getCustomerById(state, runbook.orgPk)

    const templatesState = state.runbooks.templates

    return {
        runbookId: activeRunbook,
        organization: runbook.organization,
        name: runbook.name,
        customersLoading: state.cmdb.customers.loading,
        customers,
        selectedCustomer,
        orgPk: runbook.orgPk,
        mode: runbook.mode,
        parameters: runbook.parameters,
        modalValid: validate(state, activeRunbook),
        currentUser: state.app.user,
        runbookFrameworkTemplates: templatesState.values.map(
            (id) => templatesState.byId[id]
        ),
        templatesLoading: templatesState.loading,
        selectedTemplateActions: templatesState.selectedTemplateActions,
        loadingTemplateActions: templatesState.loadingSelectedTemplateActions,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                searchCustomer,
                setActiveRunbook,
                updateRunbook: runbookModules.updateRunbook,
                getSolutions: runbookModules.getCustomerSolutions,
                generateFromCsv: runbookModules.generateRunbookFromCsv,
                getRunbookTemplates:
                    runbookModules.getRunbookTemplatesFromServer,
                getRunbookTemplateActions:
                    runbookModules.getRunbookTemplateActionsFromServer,
                applyTemplateActions: runbookModules.applyTemplateActions,
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateRunbookModal)
